import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Calendar, DeviceMobile } from "tabler-icons-react";
import { useTranslation } from "react-i18next";
import mockup1 from "../../assets/trainers/mockup-left.png";
import mockup2 from "../../assets/trainers/mockup-right.png";
import mockup3 from "../../assets/trainers/mockup-centre.png";

const primary = "#43BD98";
const accent = "#4A90E2";

export default function TrainerHero() {
	const { t } = useTranslation();
	const [start, setStart] = useState(false);

	useEffect(() => {
		const tmo = setTimeout(() => setStart(true));
		return () => clearTimeout(tmo);
	}, []);

	const features = t("trainer.hero.features", { returnObjects: true });

	return (
		<section
			id="inicio"
			className="relative bg-gradient-to-br from-primary/10 via-white to-primary/5 overflow-hidden"
		>
			<div className="max-w-7xl mx-auto px-6 lg:px-16 pt-32 md:pt-48 pb-20 grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
				<div className="text-center lg:text-left">
					<motion.h1
						key={start}
						initial={{ opacity: 0, y: 20 }}
						animate={start ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.8, delay: 0.2 }}
						style={{
							fontSize: "clamp(32px, 7vw, 46px)",
							marginBottom: "16px",
							color: "#222",
							fontWeight: 800,
							lineHeight: 1.1,
						}}
						dangerouslySetInnerHTML={{ __html: t("trainer.hero.title") }}
					/>

					<motion.p
						style={{
							fontSize: "18px",
							lineHeight: "1.6",
							color: "rgb(75, 85, 99)",
						}}
						initial={{ opacity: 0, y: 20 }}
						animate={start ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.8, delay: 0.4 }}
					>
						{t("trainer.hero.description")}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 15 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
						style={{
							display: "inline-block",
							willChange: "transform, opacity",
							transform: "translateZ(0)",
							backfaceVisibility: "hidden",
						}}
					>
						<a
							href="https://creator.applaby.com"
							target="_blank"
							className="inline-block mt-8 bg-primary text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition"
							style={{
								WebkitFontSmoothing: "antialiased",
								position: "relative",
								zIndex: 1,
							}}
							onMouseDown={(e) => e.currentTarget.blur()}
							onTouchStart={(e) => e.currentTarget.blur()}
							onFocus={(e) => e.currentTarget.blur()}
							rel="noreferrer"
						>
							{t("trainer.hero.button")}
						</a>
					</motion.div>

					<div
						style={{
							display: "flex",
							justifyContent: "flex-start",
							alignItems: "center",
							gap: "2rem",
							marginTop: "2.5rem",
							color: "#666",
							fontSize: "0.95rem",
							flexWrap: "nowrap",
							overflowX: "auto",
							scrollbarWidth: "none",
						}}
						className="features-row"
					>
						{[
							{
								icon: <Activity size={22} color={accent} />,
								text: features[0],
							},
							{
								icon: <Calendar size={22} color={accent} />,
								text: features[1],
							},
							{
								icon: <DeviceMobile size={22} color={accent} />,
								text: features[2],
							},
						].map((item, i) => {
							return (
								<span
									key={i}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
										whiteSpace: "nowrap",
										flexShrink: 0,
									}}
								>
									{item.icon}
									{item.text}
								</span>
							);
						})}

						<style>{`
							.features-row::-webkit-scrollbar {
								display: none;
							}
							@media (max-width: 768px) {
								.features-row {
									flex-wrap: wrap !important;
									justify-content: center !important;
									row-gap: 1rem !important;
								}
							}
						`}</style>
					</div>
				</div>

				<div className="relative w-full flex justify-center">
					<div className="flex md:hidden items-end justify-center relative h-[320px]">
						<motion.img
							src={mockup2}
							alt="App izquierda"
							className="absolute left-1/2 bottom-0 w-48 h-auto drop-shadow-2xl -translate-x-[70%] z-10"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
						/>
						<motion.img
							src={mockup1}
							alt="App central"
							className="w-56 h-auto drop-shadow-2xl relative z-20"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 20 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						/>
						<motion.img
							src={mockup3}
							alt="App derecha"
							className="absolute right-1/2 bottom-0 w-48 h-auto drop-shadow-2xl translate-x-[70%] z-10"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.4 }}
						/>
					</div>

					<div className="hidden md:flex items-end justify-center -space-x-32 lg:-space-x-40 ">
						<motion.img
							src={mockup2}
							alt="App izquierda"
							className="w-44 lg:w-64 h-auto drop-shadow-2xl -rotate-6 z-20"
							initial={{ opacity: 0, x: -100, y: 30 }}
							animate={{ opacity: 1, x: 0, y: [0, 6, 0], rotate: -6 }}
							transition={{
								opacity: { duration: 0.6, delay: 0.2 },
								x: { duration: 0.6, delay: 0.2 },
								y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
							}}
						/>
						<motion.img
							src={mockup1}
							alt="App central"
							className="w-52 lg:w-72 h-auto drop-shadow-2xl z-30"
							initial={{ opacity: 0, y: 80 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
						/>
						<motion.img
							src={mockup3}
							alt="App derecha"
							className="w-44 lg:w-64 h-auto drop-shadow-2xl rotate-6 z-20"
							initial={{ opacity: 0, x: 100, y: 30 }}
							animate={{ opacity: 1, x: 0, y: [0, 6, 0], rotate: 6 }}
							transition={{
								opacity: { duration: 0.6, delay: 0.4 },
								x: { duration: 0.6, delay: 0.4 },
								y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
							}}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
